/**
 * Chat Service for JonesCRM
 * Handles chat messaging between users
 */

const CHAT_API_BASE = '/api/chat';

class ChatService {
  constructor() {
    this.pollingInterval = null;
    this.unreadPollingInterval = null;
    this.groupPollingInterval = null;
    this.listeners = [];
  }

  /**
   * Get users available for chat (excludes current user)
   */
  async getChatUsers(currentUserId) {
    try {
      const url = currentUserId
        ? `${CHAT_API_BASE}/users.php?current_user_id=${currentUserId}`
        : `${CHAT_API_BASE}/users.php`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch chat users');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching chat users:', error);
      throw error;
    }
  }

  /**
   * Get user's chat preferences
   */
  async getPreferences(userId) {
    try {
      const response = await fetch(`${CHAT_API_BASE}/preferences.php?user_id=${userId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch preferences');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching preferences:', error);
      throw error;
    }
  }

  /**
   * Update user's chat preferences
   */
  async updatePreferences(userId, autoOpenEnabled) {
    try {
      const response = await fetch(`${CHAT_API_BASE}/preferences.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          auto_open_enabled: autoOpenEnabled,
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to update preferences');
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  }

  /**
   * Get unread message count for user
   */
  async getUnreadCount(userId) {
    try {
      const response = await fetch(
        `${CHAT_API_BASE}/messages.php?current_user_id=${userId}&action=unread_count`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch unread count');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  }

  /**
   * Get unread message counts per user (for user list badges)
   */
  async getUnreadCountsPerUser(userId) {
    try {
      const response = await fetch(
        `${CHAT_API_BASE}/messages.php?current_user_id=${userId}&action=unread_per_user`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch per-user unread counts');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching per-user unread counts:', error);
      throw error;
    }
  }

  /**
   * Check for new unread messages (for auto-open feature)
   */
  async checkNewMessages(userId) {
    try {
      const response = await fetch(
        `${CHAT_API_BASE}/messages.php?current_user_id=${userId}&action=check_new`
      );
      if (!response.ok) {
        throw new Error('Failed to check new messages');
      }
      return await response.json();
    } catch (error) {
      console.error('Error checking new messages:', error);
      throw error;
    }
  }

  /**
   * Start polling for unread messages (for badge and auto-open)
   */
  startUnreadPolling(userId, callback, interval = 10000) {
    this.stopUnreadPolling();

    const poll = async () => {
      try {
        const result = await this.getUnreadCount(userId);
        callback(result);
      } catch (error) {
        console.error('Unread polling error:', error);
      }
    };

    // Initial fetch
    poll();

    // Set up interval
    this.unreadPollingInterval = setInterval(poll, interval);
  }

  /**
   * Stop polling for unread messages
   */
  stopUnreadPolling() {
    if (this.unreadPollingInterval) {
      clearInterval(this.unreadPollingInterval);
      this.unreadPollingInterval = null;
    }
  }

  /**
   * Get messages between current user and another user
   */
  async getMessages(currentUserId, otherUserId) {
    try {
      const response = await fetch(
        `${CHAT_API_BASE}/messages.php?current_user_id=${currentUserId}&other_user_id=${otherUserId}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  }

  /**
   * Send a message to another user
   */
  async sendMessage(senderId, recipientId, message) {
    try {
      const response = await fetch(`${CHAT_API_BASE}/messages.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sender_id: senderId,
          recipient_id: recipientId,
          message: message,
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      return await response.json();
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  /**
   * Start polling for new messages
   */
  startPolling(currentUserId, otherUserId, callback, interval = 5000) {
    this.stopPolling();

    const poll = async () => {
      try {
        const messages = await this.getMessages(currentUserId, otherUserId);
        callback(messages);
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    // Initial fetch
    poll();

    // Set up interval
    this.pollingInterval = setInterval(poll, interval);
  }

  /**
   * Stop polling for messages
   */
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  // ===== GROUP MANAGEMENT =====

  /**
   * Get all groups for a user
   */
  async getGroups(userId) {
    try {
      const response = await fetch(`${CHAT_API_BASE}/groups.php?user_id=${userId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch groups');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching groups:', error);
      throw error;
    }
  }

  /**
   * Get group details
   */
  async getGroupDetails(groupId) {
    try {
      const response = await fetch(`${CHAT_API_BASE}/groups.php?group_id=${groupId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch group details');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching group details:', error);
      throw error;
    }
  }

  /**
   * Get group members
   */
  async getGroupMembers(groupId) {
    try {
      const response = await fetch(
        `${CHAT_API_BASE}/groups.php?group_id=${groupId}&action=members`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch group members');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching group members:', error);
      throw error;
    }
  }

  /**
   * Create a new group
   */
  async createGroup(name, createdBy, memberIds) {
    try {
      const response = await fetch(`${CHAT_API_BASE}/groups.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name,
          created_by: createdBy,
          members: memberIds,
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to create group');
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating group:', error);
      throw error;
    }
  }

  /**
   * Add member to group
   */
  async addGroupMember(groupId, userId) {
    try {
      const response = await fetch(`${CHAT_API_BASE}/groups.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          group_id: groupId,
          action: 'add_member',
          user_id: userId,
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to add member');
      }
      return await response.json();
    } catch (error) {
      console.error('Error adding member:', error);
      throw error;
    }
  }

  /**
   * Remove member from group
   */
  async removeGroupMember(groupId, userId) {
    try {
      const response = await fetch(`${CHAT_API_BASE}/groups.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          group_id: groupId,
          action: 'remove_member',
          user_id: userId,
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to remove member');
      }
      return await response.json();
    } catch (error) {
      console.error('Error removing member:', error);
      throw error;
    }
  }

  /**
   * Delete group (admin only)
   */
  async deleteGroup(groupId) {
    try {
      const response = await fetch(
        `${CHAT_API_BASE}/groups.php?group_id=${groupId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw new Error('Failed to delete group');
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting group:', error);
      throw error;
    }
  }

  // ===== GROUP MESSAGES =====

  /**
   * Get messages for a group
   */
  async getGroupMessages(groupId, currentUserId) {
    try {
      const response = await fetch(
        `${CHAT_API_BASE}/messages.php?group_id=${groupId}&current_user_id=${currentUserId}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch group messages');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching group messages:', error);
      throw error;
    }
  }

  /**
   * Send message to group
   */
  async sendGroupMessage(groupId, senderId, message) {
    try {
      const response = await fetch(`${CHAT_API_BASE}/messages.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          group_id: groupId,
          sender_id: senderId,
          message: message,
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to send group message');
      }
      return await response.json();
    } catch (error) {
      console.error('Error sending group message:', error);
      throw error;
    }
  }

  /**
   * Get unread counts per group
   */
  async getUnreadCountsPerGroup(userId) {
    try {
      const response = await fetch(
        `${CHAT_API_BASE}/messages.php?action=unread_per_group&current_user_id=${userId}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch group unread counts');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching group unread counts:', error);
      throw error;
    }
  }

  // ===== GROUP POLLING =====

  /**
   * Start polling for group messages
   */
  startGroupPolling(currentUserId, groupId, callback, interval = 5000) {
    this.stopGroupPolling();

    const poll = async () => {
      try {
        const messages = await this.getGroupMessages(groupId, currentUserId);
        callback(messages);
      } catch (error) {
        console.error('Group polling error:', error);
      }
    };

    // Initial fetch
    poll();

    // Set up interval
    this.groupPollingInterval = setInterval(poll, interval);
  }

  /**
   * Stop group message polling
   */
  stopGroupPolling() {
    if (this.groupPollingInterval) {
      clearInterval(this.groupPollingInterval);
      this.groupPollingInterval = null;
    }
  }
}

// Create singleton instance
const chatService = new ChatService();

export default chatService;
